<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";
import { maskCurrency, maskCurrencyToFloat } from "@/common/utils/mask";

import {
	planPriceList,
	planPriceCreate,
	planPricePatch,
	planPriceDelete,
} from "@/common/api/rest/plan/price";

import type {
	PlanPriceType,
	PlanPricePayloadType,
} from "@/common/types/plan/price";

import { PanelTheme } from "@/common/themes";
import {
	breadcrumbSimple,
	baseModal,
	baseTable,
	iconMenu,
	svgIcon,
} from "@/common/components";

import FormPlanPrice from "@/components/forms/form-plan-price.vue";

const { t, tm, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const tableData = ref<PlanPriceType[]>([]);
const selected = ref<PlanPriceType | null>(null);
const payload = ref<PlanPricePayloadType | null>(null);

const limit = ref<number>(6);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const showModalAdd = ref(false);
const showModalEdit = ref(false);
const showModalDel = ref(false);
const payloadValid = ref(false);
const loading = ref(false);

const planId = computed(() => route.params.plan as string);

const breadcrumbList = computed(() => [
	{
		key: "register",
		label: t("common.labels.registrations"),
		url: "/register",
	},
	{
		key: "plans",
		label: t("common.labels.plans"),
		url: "/register/plan",
	},
	{
		key: "edit",
		label: t("common.labels.edit"),
	},
]);

const menuList = computed(() => [
	{
		key: "infos",
		icon: "field",
		label: t("collection.labels.infos"),
		url: `/register/plan/${planId.value}/edit`,
	},
	{
		key: "price",
		icon: "no-image",
		label: "Preços",
		url: `/register/plan/${planId.value}/price`,
	},
	{
		key: "term",
		icon: "no-image",
		label: "Termos",
		url: `/register/plan/${planId.value}/term`,
	},
	{
		key: "module",
		icon: "no-image",
		label: "Módulos",
		url: `/register/plan/${planId.value}/module`,
	},
]);

const tableHeader = computed(() => [
	{ key: "name", label: t("common.labels.name") },
	{ key: "status", label: t("common.labels.status") },
	{ key: "currency", label: t("common.labels.currency") },
	{ key: "price", label: t("common.labels.price") },
	{ key: "frequency", label: "Frequência" },
]);

const statusOptions = computed(() => ({
	ACTIVE: t("common.labels.active"),
	INACTIVE: t("common.labels.inactive"),
}));

const frequencyOptions = computed(() => ({
	MONTHLY: "Mensal",
	SEMESTER: "Semestral",
	YEARLY: "Anual",
}));

const currencies = computed(
	() => tm("enums.currencies") as Record<string, string>,
);

async function changeMenu(url: string) {
	router.push(url);
}

function back() {
	router.push(`/register/plan`);
}

function formatCurrency(value: number, currency: any) {
	const price = Number.parseFloat(value?.toString()).toFixed(2);
	return maskCurrency(price, currency);
}

function fetchData() {
	planPriceList(planId.value, {
		page: page.value,
		limit: limit.value,
		search: search.value,
	})
		.then((response) => {
			const { success, data, message } = response;
			if (success) {
				tableData.value = data.list.map((item: PlanPriceType) => {
					const newPrice = item.price ? item.price / 100 : 0;
					return { ...item, price: newPrice };
				});

				pages.value = data.infos.pages;
				total.value = data.infos.total;
				searched.value = data.infos.count;
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		});
}

function changePage(value: number) {
	page.value = value;
	fetchData();
}

function searchItem(value: string) {
	search.value = value;
	fetchData();
}

function addItem() {
	showModalAdd.value = true;
}

function addItemCancel() {
	showModalAdd.value = false;
	payload.value = null;
}

function addItemConfirm() {
	if (!payload.value) return;

	store.commit("SET_LOADING", true);

	const priceStr = String(payload.value.price)
		.replace(/\D/g, "")
		.replace(".", "")
		.replace(",", "");

	const priceCents = Math.round(parseFloat(priceStr));

	console.log("priceStr", priceStr, "priceCents", priceCents);

	const newPayload = {
		...payload.value,
		plan_id: planId.value,
		price: priceCents,
	};

	planPriceCreate(newPayload)
		.then((response) => {
			const { success, data, message } = response;

			if (success) {
				toast({ message: message, type: "success" });
				addItemCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function editItem(data: PlanPriceType) {
	showModalEdit.value = true;
	selected.value = data;
	payload.value = null;
}

function editItemCancel() {
	showModalEdit.value = false;
	selected.value = null;
	payload.value = null;
}

function editItemConfirm() {
	if (!selected.value || !payload.value || !payloadValid.value) return;

	store.commit("SET_LOADING", true);

	let newPayload = { ...payload.value };

	if (Object.keys(payload.value).find((key) => key === "price")) {
		const priceStr = String(payload.value.price)
			.replace(/\D/g, "")
			.replace(".", "")
			.replace(",", "");
		const priceCents = Math.round(parseFloat(priceStr));

		newPayload = {
			...newPayload,
			price: priceCents,
		};
	}

	planPricePatch(selected.value.price_id, newPayload)
		.then((response) => {
			const { success, message } = response;

			if (success) {
				toast({ message: message, type: "success" });
				editItemCancel();
				fetchData();
			} else {
				toast({ message: message, type: "error" });
			}
		})
		.catch(() => {
			toast({ message: t("error.default"), type: "error" });
		})
		.finally(() => {
			store.commit("SET_LOADING", false);
		});
}

function removeItem(data: PlanPriceType) {
	selected.value = data;
	showModalDel.value = true;
}

onMounted(() => {
	fetchData();
});
</script>

<template>
	<article class="register-page">
		<section class="page-header">
			<breadcrumb-simple
				:items="breadcrumbList"
				:show-home="true"
				url-home="/home"
				active="edit"
			/>
		</section>

		<section class="page-content">
			<div class="tab-header">
				<div class="title">
					<svg-icon icon="plans" size="2rem" />
					{{ t("plan.labels.plan_edit") }}
				</div>

				<div class="actions">
					<button class="btn btn-default" @click="back()">
						<svg-icon icon="close" size="1rem" />
					</button>
				</div>
			</div>

			<div class="tab-content">
				<icon-menu
					:tabs="menuList"
					active="price"
					size="3rem"
					@selected="changeMenu($event)"
				/>

				<base-table
					:header="tableHeader"
					:data="tableData"
					:limit="limit"
					:page="page"
					:pages="pages"
					:total="total"
					:searched="searched"
					:loading="loading"
					:options="true"
					:del="true"
					:edit="true"
					@change:page="changePage($event)"
					@searching="searchItem($event)"
					@btn:add="addItem()"
					@btn:edit="editItem($event)"
					@btn:del="removeItem($event)"
				>
					<template #status="{ data }">
						{{ statusOptions[data.status] }}
					</template>

					<template #currency="{ data }">
						{{ currencies[data.currency] }}
					</template>

					<template #frequency="{ data }">
						{{ frequencyOptions[data.frequency]?.toUpperCase() }}
					</template>

					<template #price="{ data }">
						{{ formatCurrency(data.price, data.currency) }}
					</template>
				</base-table>

				<base-modal
					:show="showModalAdd"
					title="ADICIONAR PREÇO"
					:show-button-confirm="true"
					:button-confirm="t('common.labels.save')"
					:button-close="t('common.labels.cancel')"
					@close="addItemCancel()"
					@confirm="addItemConfirm()"
				>
					<template #content>
						<form-plan-price
							:data="selected"
							@update:data="payload = $event"
							@validate="payloadValid = $event"
						/>
					</template>
				</base-modal>

				<base-modal
					:show="showModalEdit"
					title="EDITAR PREÇO"
					:show-button-confirm="true"
					:button-confirm="t('common.labels.save')"
					:button-close="t('common.labels.cancel')"
					@close="editItemCancel()"
					@confirm="editItemConfirm()"
				>
					<template #content>
						<form-plan-price
							:data="selected"
							:is-patch="true"
							@update:data="payload = $event"
							@validate="payloadValid = $event"
						/>
					</template>
				</base-modal>
			</div>
		</section>
	</article>
</template>

<style lang="scss" scoped>
.register-page {
	display: flex;
	flex-direction: column;
	gap: 1rem;

	.page-header {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: inherit;
		background: white;
		border-radius: 0.5rem;
		padding-inline: 1rem;
		padding-block: 0.5rem;
	}

	.page-content {
		display: flex;
		flex-direction: column;
		background: white;
		border-radius: 0.5rem;

		.tab-header {
			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-between;
			gap: 0.5rem;
			padding-inline: 1.25rem;
			padding-block: 0.75rem;
			margin-block-end: 1.5rem;
			border-block-end: 1px solid var(--border);

			.title {
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: 0.5rem;
				font-size: 1.2rem;
				text-transform: uppercase;
			}

			.actions {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 0.75rem;

				button {
					width: 1.75rem;
					height: 1.75rem;
					padding: 0;
				}
			}
		}

		.tab-content {
			display: flex;
			flex-direction: column;
			gap: 1.54rem;
			padding-inline: 1.25rem;
			padding-block-end: 1.25rem;

			&-actions {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				gap: 1rem;
			}

			&-title {
				font-size: 1.25rem;
				font-weight: 600;
				text-transform: uppercase;
				color: var(--text-medium);
				margin-block-end: 0.75rem;
			}

			&-list {
				display: grid;
				grid-template-columns: repeat(2, minmax(200px, 1fr));
				gap: 1rem;

				&-item {
					flex: 1 1 200px;
					display: flex;
					flex-direction: column;
					gap: 0.75rem;
					border: 2px solid var(--border);
					border-radius: 0.5rem;
					padding-block: 1rem;
					padding-inline: 1.25rem;
					cursor: pointer;

					&:hover {
						border-color: var(--primary);
					}

					&-title {
						font-size: 1.25rem;
						font-weight: 600;
						text-transform: uppercase;
					}

					&-description {
						font-size: 1rem;
						color: var(--text-light);
					}
				}
			}
		}
	}
}
</style>
